"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { ArtistActiveSubmissionContent } from "./ArtistActiveSubmissionContent";
import { ArtistWinningArtworksContent } from "./ArtistWinningArtworksContent";
import { ArtistPreviewModalHeader } from "./ArtistPreviewModalHeader";
import { ArtistPreviewModalTabs } from "./ArtistPreviewModalTabs";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ModalTab } from "./ArtistPreviewModal";
import { useCompactMode } from "@/contexts/CompactModeContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useIdentity } from "@/hooks/useIdentity";
interface ArtistPreviewModalContentProps {
  readonly user: ApiProfileMin;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly isApp?: boolean | undefined;
  readonly activeTab: ModalTab;
  readonly onTabChange: (tab: ModalTab) => void;
  readonly hasTrophyArtworks: boolean;
}
export const ArtistPreviewModalContent: React.FC<ArtistPreviewModalContentProps> = t0 => {
  const $ = _c(49);
  const {
    user,
    isOpen,
    onClose,
    isApp: t1,
    activeTab,
    onTabChange,
    hasTrophyArtworks
  } = t0;
  const isApp = t1 === undefined ? false : t1;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const compact = useCompactMode();
  const isSmallScreen = useMediaQuery("(max-width: 1023px)");
  const submissionCount = getSubmissionCount(user);
  const trophyCount = getTrophyArtworkCount(user);
  const knownSubmissionIdsCount = user.active_main_stage_submission_ids.length;
  const knownTrophyIdsCount = user.winner_main_stage_drop_ids.length + user.artist_of_prevote_cards.length;
  const identityKey = user.handle ?? user.primary_address;
  const shouldHydrateArtistActivity = isOpen && !!identityKey && (submissionCount > knownSubmissionIdsCount || trophyCount > knownTrophyIdsCount);
  const t2 = shouldHydrateArtistActivity ? identityKey : undefined;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      handleOrWallet: t2,
      initialProfile: null
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    profile: hydratedIdentity,
    isLoading: isHydratingArtistActivity
  } = useIdentity(t3);
  let t4;
  if ($[2] !== hydratedIdentity || $[3] !== user) {
    t4 = mergeArtistActivity(user, hydratedIdentity);
    $[2] = hydratedIdentity;
    $[3] = user;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const hydratedUser = t4;
  const hydratedSubmissionCount = getSubmissionCount(hydratedUser);
  const hydratedTrophyCount = getTrophyArtworkCount(hydratedUser);
  const hasActiveSubmissions = hydratedSubmissionCount > 0;
  const hasHydratedTrophyArtworks = hasTrophyArtworks || hydratedTrophyCount > 0;
  const showTabs = hasActiveSubmissions && hasHydratedTrophyArtworks;
  let currentContentType;
  if (showTabs) {
    currentContentType = activeTab;
  } else {
    if (hasHydratedTrophyArtworks) {
      currentContentType = "winners";
    } else {
      currentContentType = "active";
    }
  }
  let t5;
  if ($[5] !== compact || $[6] !== isSmallScreen || $[7] !== onClose || $[8] !== pathname || $[9] !== router || $[10] !== searchParams) {
    t5 = drop => {
      if (searchParams) {
        const params = new URLSearchParams(searchParams.toString());
        params.set("drop", drop.id);
        router.push(`${pathname}?${params.toString()}`);
      }
      if (compact && isSmallScreen && globalThis.window !== undefined) {
        globalThis.window.dispatchEvent(new CustomEvent("single-drop:close-chat"));
      }
      onClose();
    };
    $[5] = compact;
    $[6] = isSmallScreen;
    $[7] = onClose;
    $[8] = pathname;
    $[9] = router;
    $[10] = searchParams;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleDropClick = t5;
  let t6;
  if ($[12] !== currentContentType) {
    t6 = currentContentType === "winners" && <><div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-h-12 tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0)_70%)]" /><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-backdrop-blur-[1px]" /></>;
    $[12] = currentContentType;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== currentContentType || $[15] !== hydratedSubmissionCount || $[16] !== hydratedTrophyCount || $[17] !== hydratedUser || $[18] !== isApp || $[19] !== onClose) {
    t7 = <ArtistPreviewModalHeader user={hydratedUser} onClose={onClose} isApp={isApp} currentContentType={currentContentType} submissionCount={hydratedSubmissionCount} trophyCount={hydratedTrophyCount} />;
    $[14] = currentContentType;
    $[15] = hydratedSubmissionCount;
    $[16] = hydratedTrophyCount;
    $[17] = hydratedUser;
    $[18] = isApp;
    $[19] = onClose;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== activeTab || $[22] !== onTabChange || $[23] !== showTabs) {
    t8 = showTabs && <ArtistPreviewModalTabs activeTab={activeTab} onTabChange={onTabChange} />;
    $[21] = activeTab;
    $[22] = onTabChange;
    $[23] = showTabs;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  bb0: {
    if (isHydratingArtistActivity) {
      let t10;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <ArtistActivityLoadingState />;
        $[25] = t10;
      } else {
        t10 = $[25];
      }
      t9 = t10;
      break bb0;
    }
    if (showTabs) {
      let t10;
      if ($[26] !== activeTab || $[27] !== handleDropClick || $[28] !== hydratedUser || $[29] !== isApp || $[30] !== isOpen || $[31] !== onClose) {
        t10 = activeTab === "active" ? <ArtistActiveSubmissionContent user={hydratedUser} isOpen={isOpen} onClose={onClose} isApp={isApp} /> : <ArtistWinningArtworksContent user={hydratedUser} isOpen={isOpen} onDropClick={handleDropClick} />;
        $[26] = activeTab;
        $[27] = handleDropClick;
        $[28] = hydratedUser;
        $[29] = isApp;
        $[30] = isOpen;
        $[31] = onClose;
        $[32] = t10;
      } else {
        t10 = $[32];
      }
      t9 = t10;
      break bb0;
    }
    if (hasHydratedTrophyArtworks) {
      let t10;
      if ($[33] !== handleDropClick || $[34] !== hydratedUser || $[35] !== isOpen) {
        t10 = <ArtistWinningArtworksContent user={hydratedUser} isOpen={isOpen} onDropClick={handleDropClick} />;
        $[33] = handleDropClick;
        $[34] = hydratedUser;
        $[35] = isOpen;
        $[36] = t10;
      } else {
        t10 = $[36];
      }
      t9 = t10;
      break bb0;
    }
    let t10;
    if ($[37] !== hydratedUser || $[38] !== isApp || $[39] !== isOpen || $[40] !== onClose) {
      t10 = <ArtistActiveSubmissionContent user={hydratedUser} isOpen={isOpen} onClose={onClose} isApp={isApp} />;
      $[37] = hydratedUser;
      $[38] = isApp;
      $[39] = isOpen;
      $[40] = onClose;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    t9 = t10;
  }
  let t10;
  if ($[42] !== t8 || $[43] !== t9) {
    t10 = <div className="tw-relative tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300">{t8}{t9}</div>;
    $[42] = t8;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== t10 || $[46] !== t6 || $[47] !== t7) {
    t11 = <div className="tailwind-scope tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-white/5 tw-bg-[#0E1012] tw-shadow-[0_10px_40px_rgba(0,0,0,0.55)]">{t6}{t7}{t10}</div>;
    $[45] = t10;
    $[46] = t6;
    $[47] = t7;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
};
const mergeArtistActivity = (user: ApiProfileMin, identity: ApiIdentity | null): ApiProfileMin => {
  if (!identity) {
    return user;
  }
  return {
    ...user,
    active_main_stage_submission_ids: identity.active_main_stage_submission_ids,
    winner_main_stage_drop_ids: identity.winner_main_stage_drop_ids,
    artist_of_prevote_cards: identity.artist_of_prevote_cards,
    profile_wave_id: identity.profile_wave_id ?? user.profile_wave_id,
    is_wave_creator: identity.is_wave_creator || user.is_wave_creator
  };
};
const ArtistActivityLoadingState = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-h-96 tw-items-center tw-justify-center"><div className="tw-flex tw-flex-col tw-items-center tw-gap-4"><div className="tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-400" /><span className="tw-animate-fade-in-out tw-text-sm tw-text-iron-400">Loading artist activity...</span></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};